import type { TourHistoryDay, TourHistoryItem, TourPeriodValue } from "../api/ToursApi";
import type { TourDateGroup, TourPeriod, TourSlot } from "../types/history";
import { NOMINAL_RUN_HOUR, PERIOD_ORDER, TIMEZONE } from "../constants/periods";
import { aggregateCardStatus } from "./aggregateCardStatus";

interface AlmatyParts {
  date: string; // "YYYY-MM-DD"
  hour: number; // 0-23
}

// Детерминированно извлекаем дату и час в часовом поясе Алматы через Intl
// (Алматы — фиксированный UTC+5, без перехода на летнее время).
function almatyParts(iso: string): AlmatyParts {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";

  // Intl может вернуть час "24" в полночь — нормализуем к 0.
  const hour = Number(get("hour")) % 24;

  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    hour,
  };
}

const EMPTY_PERIOD: TourPeriodValue = {
  id: null,
  post: null,
  status: null,
  filter: null,
  createdAt: "",
};

/**
 * Преобразует ответ /history (предсгруппированный по дням и периодам) в структуру UI,
 * добавляя плейсхолдеры для пустых слотов.
 *
 * - Каждый период содержит ровно один прогон или объект-заглушку (id === null) от бэкенда.
 * - Для сегодняшней даты пустые слоты дополняются плейсхолдерами.
 * - Сегодняшняя дата всегда присутствует. Даты сортируются по убыванию.
 */
export function mapHistoryToGroups(
  days: TourHistoryDay[],
  now: Date = new Date()
): TourDateGroup[] {
  const today = almatyParts(now.toISOString());

  const buckets = new Map<string, Record<TourPeriod, TourPeriodValue>>();

  for (const day of days) {
    buckets.set(day.date, {
      morning: day.periods.morning,
      afternoon: day.periods.afternoon,
      evening: day.periods.evening,
    });
  }

  // Гарантируем, что сегодняшняя дата присутствует даже без единого прогона.
  if (!buckets.has(today.date)) {
    buckets.set(today.date, {
      morning: EMPTY_PERIOD,
      afternoon: EMPTY_PERIOD,
      evening: EMPTY_PERIOD,
    });
  }

  const dates = Array.from(buckets.keys()).sort((a, b) => (a < b ? 1 : -1));

  return dates.map((date) => {
    const dayBucket = buckets.get(date)!;
    const isToday = date === today.date;

    const slots = {} as Record<TourPeriod, TourSlot>;

    for (const period of PERIOD_ORDER) {
      const periodItem = dayBucket[period];

      if (periodItem.id !== null) {
        // Бэкенд гарантирует: id !== null ⟹ остальные поля тоже не null.
        const item = periodItem as TourHistoryItem;
        slots[period] = {
          period,
          card: { period, status: aggregateCardStatus([item]), items: [item] },
          placeholder: null,
        };
      } else if (isToday) {
        const runHasPassed = today.hour >= NOMINAL_RUN_HOUR[period];
        slots[period] = {
          period,
          card: null,
          placeholder: runHasPassed ? "missed" : "planned",
        };
      } else {
        // Прошлые даты: пустой слот помечаем missed; секция отрисует пустую ячейку.
        slots[period] = { period, card: null, placeholder: "missed" };
      }
    }

    return { date, slots };
  });
}
